import { PosterStage } from "./stage/PosterStage";
import styles from "./PosterEditor.module.css";
import { PosterProjectProvider } from "./model/poster-project-provider";
import { EditorUIProvider } from "./use-editor-ui";
import { PosterEditorToolbar } from "./sidebar/poster-editor-toolbar";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useTemplateStore } from "entity-store/template-store/use-template-store";
import { PosterProject } from "./model/poster-project";
import { ProjectTemplate } from "./model/project-template";
import { useState, useEffect } from "react";
import { useAuth } from "BEDROCK/auth/supabase-auth";
import { usePaths } from "BEDROCK/paths";
import { useCheckout } from "BEDROCK/pages/checkout/checkout-provider";
import { usePosterProject } from "./model/poster-project-provider";
import { isUserAuthenticated } from "BEDROCK/auth/auth";
import { add } from "lodash";

export type EditorContextType = "editing" | "rendering";

interface PosterEditorProps {
  //Will likely be passed in when used as a direct component rather than a linked page.
  //So this will probably be when it's nested in process-order.
  projectID?: string;
  contextType?: EditorContextType; //Remove
}

export const PosterEditor = (props: PosterEditorProps) => {
  const { loadProject, isError } = useProjectStore();

  const {
    loadTemplate,
    isLoading: isLoadingTemplate,
    isFetching: isFetchingTemplate,
    isError: isErrorTemplate,
  } = useTemplateStore();

  const [initialProject, setInitialProject] = useState<
    PosterProject | undefined
  >();
  const [template, setTemplate] = useState<ProjectTemplate | undefined>();
  const [contextType, setContextType] = useState<EditorContextType>("editing");

  //Get the id from the URL last part and load project or template
  useEffect(() => {
    const fetchData = async () => {
      if (props.projectID) {
        const project = await loadProject(props.projectID);
        setInitialProject(project);
      } else {
        const path = window.location.pathname;
        const parts = path.split("/");
        const id = parts[parts.length - 1];
        const type = parts[parts.length - 2]; // Get the segment before the ID
        //Check if the url parameter 'process' is set to true
        const process = new URLSearchParams(window.location.search).get(
          "process"
        );

        if (!id) return;

        if (type === "project") {
          const project = await loadProject(id);
          setInitialProject(project);
          if (process === "true") {
            //Remote url params
            // Clean up the URL
            const url = new URL(window.location.href);
            url.searchParams.delete("process");
            window.history.replaceState(
              {},
              document.title,
              url.pathname + url.search
            );
            setContextType("rendering");
          }
        } else if (type === "template") {
          const template = await loadTemplate(id); // Ensure `loadTemplate` exists in the relevant storage hook
          setTemplate(template);
        }
      }
    };

    fetchData();
  }, []);

  if (isLoadingTemplate || isFetchingTemplate) {
    return <Loading />;
  }

  const error = isError || isErrorTemplate;
  const object = initialProject || template;

  if (!object) {
    return <NoProjectFound />;
  }
  return (
    <PosterProjectProvider
      initialProject={template?.project ?? initialProject}
      projectTemplate={template}
    >
      <Content
        contextType={contextType}
        onSave={() => {
          setContextType("rendering");
        }}
      />
    </PosterProjectProvider>
  );
};

const NoProjectFound = () => {
  return <div>No project found</div>;
};

const Loading = () => {
  return <div>Loading...</div>;
};

interface ContentProps {
  contextType: EditorContextType;
  onSave: () => void;
}
const Content = (props: ContentProps) => {
  const { project } = usePosterProject();
  const { signIn, checkout, navigateTo, mapEditor } = usePaths();
  const { isUserAuthenticated } = useAuth();
  const { addProject: addProjectToCheckout } = useCheckout();

  const handleSave = () => {
    if (!isUserAuthenticated) {
      addProjectToCheckout(project.id);
      const redirectPath = `${mapEditor}/${project.id}?process=true`;
      const path = `${signIn}?redirect=${encodeURIComponent(redirectPath)}`;
      navigateTo(path);
    } else {
      addProjectToCheckout(project.id);
      props.onSave();
    }
  };

  return (
    <EditorUIProvider>
      <div className={styles.container}>
        {props.contextType !== "rendering" && (
          <div className={styles.sidebarContainer}>
            <PosterEditorToolbar onClickSave={handleSave} />
          </div>
        )}
        <div className={styles.editorStageContainer}>
          <PosterStage
            onShowContextualEditor={(show) => {}}
            contextType={props.contextType}
          />
        </div>
      </div>
    </EditorUIProvider>
  );
};
